import {formatNumber} from '@angular/common';

/**
 * Money and percent formats of the OCA-E-159 dashboard (ITP-E-147-03; mockup format.ts:12-30). All money is USD.
 * Hand-rolled rather than Intl's compact notation, whose output differs between ICU versions.
 */

/** A missing or non-finite number counts as 0. */
const finite = (v: number): number => (Number.isFinite(v) ? v : 0);

/** Compact USD: $950, $1.2k, $12.4k, $186k, $3.46M, $12.5M; negatives "-$1.2k". */
export function adbUsdCompact(v: number): string {
  const value = finite(v);
  const a = Math.abs(value);
  const sign = value < 0 && Math.round(a) > 0 ? '-' : '';
  if (a >= 1000000) {
    return `${sign}$${(a / 1000000).toFixed(a >= 10000000 ? 1 : 2)}M`;
  }
  if (a >= 10000) {
    return `${sign}$${(a / 1000).toFixed(a >= 100000 ? 0 : 1)}k`;
  }
  if (a >= 1000) {
    return `${sign}$${(a / 1000).toFixed(1)}k`;
  }
  return `${sign}$${Math.round(a)}`;
}

/** $186,400 - the whole figure, for tooltips and tables; negatives "-$1,235". */
export function adbUsdFull(v: number): string {
  const value = finite(v);
  const a = Math.round(Math.abs(value));
  return `${value < 0 && a > 0 ? '-' : ''}$${formatNumber(a, 'en-US', '1.0-0')}`;
}

/** 0.123 → "12%"; with digits, 0.1234, 1 → "12.3%". */
export function adbPct(ratio: number, digits = 0): string {
  return `${(finite(ratio) * 100).toFixed(digits)}%`;
}

/** The change from prev to cur: "+12%" / "−8%" (U+2212 minus); "" when prev is 0 or either value is not finite. */
export function adbChange(cur: number, prev: number): string {
  if (!Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) {
    return '';
  }
  const change = (cur - prev) / prev;
  return `${change >= 0 ? '+' : '−'}${adbPct(Math.abs(change))}`;
}
